/**
 * A hold that keeps `WaiverGate` from swapping the route tree out from under a
 * checkout finish step.
 *
 * The guest path signs the waiver ON the finish step, then creates the account
 * (which flips `isAuthenticated`), then posts the captured signatures. In the
 * window between the auth flip and the signature rows landing, the standing
 * query truthfully reports the brand-new member as unsigned — and the gate
 * would replace the entire flow with `SignWaiversScreen`, unmounting the step
 * mid-`createBooking.mutate` and losing the booking, not just the gate. Gating
 * the consent surface against itself is never right, so a mounted finish step
 * holds the gate open; `useWaiverSubmission` invalidates standing after
 * posting, so by the time the step unmounts (navigation to the confirmation)
 * the gate re-evaluates against fresh, signed standing.
 *
 * This is a ROUTE-level concern and it is the only one left: `WaiverGate` is an
 * ancestor of the whole flow and subscribes to standing itself, independently of
 * anything the step does. The step's own gate no longer needs protecting — it
 * reads `satisfied` as a value snapshot off the pre-flip render's object (see
 * useWaiverSubmission for why that, and not `await` ordering, is what makes it safe).
 *
 * Note the consequence: while a finish step is mounted, the entry gate is OFF. On
 * the booking and demo paths the step's own `satisfied` then becomes the only
 * waiver control in the system, so it has to stay live and honest — do not swap
 * it for a cached or snapshotted answer.
 *
 * The two SUBSCRIPTION holders capture no waiver at all, by design: a waiver
 * binds bay usage, not a purchase (see WaiverGate), and a subscription mints no
 * access code. For them the hold is pure suppression, and during their window
 * there is no waiver control at either level. That is intended, not an oversight.
 *
 * Module-scoped store rather than context: the gate sits OUTSIDE
 * `BookingProvider` in the route tree, so there is no shared provider to put
 * this on without restructuring the routes.
 *
 * @module features/waivers/waiverGateHold
 */

import { useEffect, useSyncExternalStore } from 'react';

let holdCount = 0;
const listeners = new Set<() => void>();

function notify() {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): boolean {
  return holdCount > 0;
}

/** Gate side: true while any finish step is mounted. */
export function useIsWaiverGateHeld(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/** Step side: hold the gate for this component's lifetime. */
export function useWaiverGateHold(): void {
  useEffect(() => {
    holdCount += 1;
    notify();
    return () => {
      holdCount -= 1;
      notify();
    };
  }, []);
}
